import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { Button } from '@/components/ui/button';
import {
  Plus, Loader2, ChevronDown, ChevronRight, Shield, UserCheck, Lock, Unlock,
  Video, Palette, Share2, Target, BarChart3, Clock, MousePointerClick, GitCompare,
  FlaskConical, Anchor, Crosshair, Globe, LayoutGrid, HelpCircle, GraduationCap,
  Handshake, MonitorPlay, ListVideo, Presentation, Gift, FileText, DollarSign,
  Users, User, FolderIcon,
} from 'lucide-react';
import {
  Member,
  BackendMemberLink,
  AccessType,
  FolderAccessEntry,
  BackendPermission,
  FEATURES,
  FEATURE_GROUP_LABELS,
  FeatureItem,
  mapBackendMemberToUI,
} from '@/types/members';

import { togglePermission as togglePerm } from '@/utils/memberPermissions';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Video, Palette, Share2, Target, BarChart3, Clock, MousePointerClick, GitCompare,
  FlaskConical, Anchor, Shield, Crosshair, Globe, LayoutGrid, HelpCircle, GraduationCap,
  Handshake, MonitorPlay, ListVideo, Presentation, Gift, FileText, DollarSign,
  Users, User, Lock,
};
import { MemberTableDesktop } from '@/components/members/MemberTableDesktop';
import { MemberCardMobile } from '@/components/members/MemberCardMobile';
import { RemoveMemberModal } from '@/components/members/RemoveMemberModal';
import { toast } from 'sonner';
import { memberService } from '@/services/memberService';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { authApi } from '@/services/authService';
import { PasswordField } from '@/components/auth/PasswordField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface FolderNode {
  id_folder: string;
  folder_name: string;
  parent_id: string | null;
  children?: FolderNode[];
}

const LEVEL_OPTIONS = [
  { value: 'none', get label() { return translateInterfaceText("Sem acesso"); } },
  { value: 'view', get label() { return translateInterfaceText("Visualização"); } },
  { value: 'edit', get label() { return translateInterfaceText("Edição"); } },
];

function buildFolderTree(flat: { id_folder: string; folder_name: string; parent_id: string | null }[]): FolderNode[] {
  const map = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];
  for (const f of flat) {
    map.set(f.id_folder, { ...f, children: [] });
  }
  for (const f of flat) {
    const node = map.get(f.id_folder)!;
    if (f.parent_id && map.has(f.parent_id)) {
      map.get(f.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function FolderTreeItem({
  node,
  depth,
  accessMap,
  onToggle,
}: {
  node: FolderNode;
  depth: number;
  accessMap: Map<string, 'view' | 'edit'>;
  onToggle: (folderId: string, level: 'view' | 'edit' | 'none') => void;
}) {
  useLanguage();
  const [open, setOpen] = useState(true);
  const level = accessMap.get(node.id_folder) || 'none';
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2 py-1.5" style={{ paddingLeft: depth * 16 }}>
        {hasChildren ? (
          <button onClick={() => setOpen(!open)} className="p-0.5 hover:bg-gray-100 rounded">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <span className="text-sm text-[#1E293B] flex-1 truncate">{node.folder_name}</span>
        <Select
          value={level}
          onValueChange={(v) => onToggle(node.id_folder, v as 'view' | 'edit' | 'none')}
        >
          <SelectTrigger className="w-[130px] h-9 text-sm border-[#E2E8F0] rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEVEL_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {open && hasChildren && node.children!.map((child) => (
        <FolderTreeItem
          key={child.id_folder}
          node={child}
          depth={depth + 1}
          accessMap={accessMap}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

function CategoryPermissionRow({
  groupKey,
  groupLabel,
  features,
  permissions,
  onToggle,
}: {
  groupKey: string;
  groupLabel: string;
  features: typeof FEATURES;
  permissions: Set<string>;
  onToggle: (featureKey: string, level: 'view' | 'edit' | 'none') => void;
}) {
  useLanguage();
  return (
    <div className="py-2.5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-[#1E293B]">{groupLabel}</span>
      </div>
      <div className="flex flex-col">
        {features.map((f) => {
          const hasView = permissions.has(`feature:${f.key}:view`);
          const hasEdit = permissions.has(`feature:${f.key}:edit`);
          const value = hasEdit ? 'edit' : hasView ? 'view' : 'none';
          const IconComp = f.icon ? ICON_MAP[f.icon] : null;
          return (
            <div key={f.key} className="flex items-center justify-between py-2.5 border-b border-[#F1F5F9] last:border-b-0">
              <div className="flex items-center gap-2.5">
                {IconComp && <IconComp className="w-4 h-4 text-[#94A3B8]" />}
                <span className="text-sm text-[#475569]">{f.label}</span>
              </div>
              <Select
                value={value}
                onValueChange={(v) => onToggle(f.key, v as 'view' | 'edit' | 'none')}
              >
                <SelectTrigger className="w-[130px] h-9 text-sm border-[#E2E8F0] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Members() {
  useLanguage();
  const { user } = useCustomAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addPasswordConfirm, setAddPasswordConfirm] = useState('');
  const [addAccessType, setAddAccessType] = useState<AccessType>('MEMBER');
  const [addFolderAccess, setAddFolderAccess] = useState<Map<string, 'view' | 'edit'>>(new Map());
  const [addPermissions, setAddPermissions] = useState<Set<string>>(new Set());

  const [editName, setEditName] = useState('');
  const [editAccessType, setEditAccessType] = useState<AccessType>('MEMBER');
  const [editFolderAccess, setEditFolderAccess] = useState<Map<string, 'view' | 'edit'>>(new Map());
  const [editPermissions, setEditPermissions] = useState<Set<string>>(new Set());

  const [folderTree, setFolderTree] = useState<FolderNode[]>([]);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await memberService.list();
      const uiMembers = data.map((link) =>
        mapBackendMemberToUI(link, user?.id || user?.id_user || ''),
      );
      setMembers(uiMembers);
    } catch (error: unknown) {
      console.error('Erro ao buscar membros:', error);
      toast.error(translateInterfaceText("Erro ao carregar membros."));
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await authApi.get('/folders/root');
      const rootFolders = res.data.data || [];
      setFolderTree(buildFolderTree(rootFolders));
    } catch (e) {
      console.error('Erro ao buscar pastas:', e);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    if (addModalOpen) fetchFolders();
  }, [addModalOpen, fetchFolders]);

  const resetAddForm = () => {
    setAddName('');
    setAddEmail('');
    setAddPassword('');
    setAddPasswordConfirm('');
    setAddAccessType('MEMBER');
    setAddFolderAccess(new Map());
    setAddPermissions(new Set());
  };

  const handleAdd = async () => {
    if (!addName.trim()) { toast.error(translateInterfaceText("Insira o nome do membro")); return; }
    if (!addEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addEmail)) { toast.error(translateInterfaceText("Insira um e-mail válido")); return; }
    if (!addPassword.trim() || addPassword.length < 6) { toast.error(translateInterfaceText("A senha deve ter pelo menos 6 caracteres")); return; }
    if (addPassword !== addPasswordConfirm) { toast.error(translateInterfaceText("As senhas não coincidem")); return; }

    try {
      setCreating(true);
      const folderAccess: FolderAccessEntry[] = [];
      addFolderAccess.forEach((level, folderId) => {
        if (level !== 'none') folderAccess.push({ folder_id: folderId, level });
      });

      const permissions: BackendPermission[] = [];
      addPermissions.forEach((p) => permissions.push(p));

      await memberService.create({
        name: addName,
        email: addEmail,
        password: addPassword,
        permissions: addAccessType === 'ADMIN' ? [] : permissions,
        accessType: addAccessType,
        folderAccess: addAccessType === 'ADMIN' ? [] : folderAccess,
      });
      toast.success(translateInterfaceText("Membro adicionado com sucesso!"));
      resetAddForm();
      setAddModalOpen(false);
      await fetchMembers();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : translateInterfaceText("Erro ao criar membro.");
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (member: Member) => {
    setSelectedMember(member);
    setEditName(member.name);
    setEditAccessType(member.access_type);
    setEditFolderAccess(new Map(member.folder_access.map((e) => [e.folder_id, e.level])));
    setEditPermissions(new Set(member.permissions));
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedMember) return;
    try {
      const folderAccess: FolderAccessEntry[] = [];
      editFolderAccess.forEach((level, folderId) => {
        if (level !== 'none') folderAccess.push({ folder_id: folderId, level });
      });

      const permissions: BackendPermission[] = [];
      editPermissions.forEach((p) => permissions.push(p));

      await memberService.update(selectedMember.id, {
        name: editName,
        accessType: editAccessType,
        permissions: editAccessType === 'ADMIN' ? [] : permissions,
        folderAccess: editAccessType === 'ADMIN' ? [] : folderAccess,
      });
      toast.success(translateInterfaceText("Membro atualizado com sucesso!"));
      setEditModalOpen(false);
      await fetchMembers();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : translateInterfaceText("Erro ao atualizar membro.");
      toast.error(msg);
    }
  };

  const handleBlock = async (member: Member) => {
    try {
      await memberService.block(member.id);
      toast.success(translateInterfaceText("Membro bloqueado com sucesso!"));
      await fetchMembers();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : translateInterfaceText("Erro ao bloquear membro.");
      toast.error(msg);
    }
  };

  const handleUnblock = async (member: Member) => {
    try {
      await memberService.unblock(member.id);
      toast.success(translateInterfaceText("Membro desbloqueado com sucesso!"));
      await fetchMembers();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : translateInterfaceText("Erro ao desbloquear membro.");
      toast.error(msg);
    }
  };

  const handleRemove = (member: Member) => {
    setSelectedMember(member);
    setRemoveModalOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!selectedMember) return;
    try {
      await memberService.remove(selectedMember.id);
      toast.success(translateInterfaceText("Membro removido com sucesso!"));
      setRemoveModalOpen(false);
      await fetchMembers();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : translateInterfaceText("Erro ao remover membro.");
      toast.error(msg);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const canManageMembers = true;

  // Grupos que não aparecem na configuração de permissões. As features seguem em
  // FEATURES: quem já tem a permissão continua com ela, só não é mais configurável aqui.
  const HIDDEN_FEATURE_GROUPS = ['extras'];

  const groupedFeatures = FEATURES.reduce<Record<string, typeof FEATURES>>((acc, f) => {
    if (f.group === 'extras') return acc;
    if (!acc[f.group]) acc[f.group] = [];
    acc[f.group].push(f);
    return acc;
  }, {});

  const toggleFolderAccess = (
    map: Map<string, 'view' | 'edit'>,
    setMap: (m: Map<string, 'view' | 'edit'>) => void,
    folderId: string,
    level: 'view' | 'edit' | 'none',
  ) => {
    const next = new Map(map);
    if (level === 'none') next.delete(folderId);
    else next.set(folderId, level);
    setMap(next);
  };

  const handleTogglePermission = (
    set: Set<string>,
    setSet: (s: Set<string>) => void,
    featureKey: string,
    level: 'view' | 'edit' | 'none',
  ) => {
    setSet(togglePerm(set, featureKey, level));
  };

  const renderPermissionModal = (
    accessType: AccessType,
    setAccessType: (t: AccessType) => void,
    folderAccess: Map<string, 'view' | 'edit'>,
    setFolderAccess: (m: Map<string, 'view' | 'edit'>) => void,
    permissions: Set<string>,
    setPermissions: (s: Set<string>) => void,
  ) => (
    <div className="space-y-5">
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-[#1E293B]">{translateInterfaceText("Permissão")}</Label>
        <RadioGroup
          value={accessType}
          onValueChange={(v) => setAccessType(v as AccessType)}
          className="grid grid-cols-2 gap-4"
        >
          <div className="flex items-center justify-center gap-2.5 p-3 rounded-xl border border-[#E2E8F0] cursor-pointer hover:border-[#3B82F6] transition-colors">
            <RadioGroupItem value="ADMIN" id="admin" />
            <Label htmlFor="admin" className="text-sm text-[#1E293B] cursor-pointer">
              {translateInterfaceText("Administrador")}</Label>
          </div>
          <div className="flex items-center justify-center gap-2.5 p-3 rounded-xl border border-[#E2E8F0] cursor-pointer hover:border-[#3B82F6] transition-colors">
            <RadioGroupItem value="MEMBER" id="member" />
            <Label htmlFor="member" className="text-sm text-[#1E293B] cursor-pointer">
              {translateInterfaceText("Usuário Comum")}</Label>
          </div>
        </RadioGroup>
        <p className="text-xs text-[#9CA3AF]">
          {accessType === 'ADMIN'
            ? translateInterfaceText("Tem todas as permissões.")
            : translateInterfaceText("Permissões limitadas atribuídas.")}
        </p>
      </div>

      {accessType === 'MEMBER' && (
        <>
          <div className="space-y-1">
            <Label className="text-sm font-semibold text-[#1E293B]">{translateInterfaceText("Permissões por Recurso")}</Label>
            {Object.entries(groupedFeatures).map(([groupKey, features]) => (
              <CategoryPermissionRow
                key={groupKey}
                groupKey={groupKey}
                groupLabel={FEATURE_GROUP_LABELS[groupKey] || groupKey}
                features={features}
                permissions={permissions}
                onToggle={(featureKey, level) => handleTogglePermission(permissions, setPermissions, featureKey, level)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title={translateInterfaceText("Membros")}
          icon={<Users className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />}
        />
        <div className="hidden lg:block h-px bg-border w-full" />
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 max-w-7xl">
            {canManageMembers && (
              <div className="flex justify-end mb-6">
                <Button
                  onClick={() => { resetAddForm(); setAddModalOpen(true); }}
                  className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1D4ED8] h-11 rounded-full px-6 font-medium"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {translateInterfaceText("Adicionar Membro")}</Button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#556ff8]" />
                <span className="ml-2 text-sm text-[#64748B]">{translateInterfaceText("Carregando membros...")}</span>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-[#64748B]">
                  {translateInterfaceText("Nenhum membro encontrado. Adicione membros para gerenciar sua equipe.")}</p>
                <p className="text-[14px] text-[#9CA3AF] font-normal mt-1">
                  {translateInterfaceText("Gerencie os membros da sua equipe. Defina permissões de acesso por feature e por pasta.")}</p>
              </div>
            ) : (
              <>
                <MemberTableDesktop
                  members={members}
                  onEdit={handleEdit}
                  onResend={() => {}}
                  onRemove={handleRemove}
                  onBlock={handleBlock}
                  onUnblock={handleUnblock}
                />
                <div className="md:hidden space-y-3">
                  {members.map((member) => (
                    <MemberCardMobile
                      key={member.id}
                      member={member}
                      onEdit={handleEdit}
                      onResend={() => {}}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Modal Adicionar Membro */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="w-[640px] min-w-[600px] max-w-[640px] rounded-2xl p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">
              {translateInterfaceText("Adicionar Membro")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="addName" className="text-sm font-semibold text-[#1E293B]">{translateInterfaceText("Nome")}</Label>
              <Input id="addName" placeholder={translateInterfaceText("Nome do membro")} value={addName} onChange={(e) => setAddName(e.target.value)} className="h-11 rounded-xl border-[#E2E8F0]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="addEmail" className="text-sm font-semibold text-[#1E293B]">{translateInterfaceText("E-mail")}</Label>
              <Input id="addEmail" type="email" placeholder="exemplo@empresa.com" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} className="h-11 rounded-xl border-[#E2E8F0]" />
            </div>
            <PasswordField id="addPassword" label={translateInterfaceText("Senha")} placeholder={translateInterfaceText("Mínimo 6 caracteres")} value={addPassword} onChange={(e) => setAddPassword(e.target.value)} showIcon={false} className="h-11 rounded-xl border-[#E2E8F0]" />
            <PasswordField id="addPasswordConfirm" label={translateInterfaceText("Confirmar senha")} placeholder={translateInterfaceText("Repita a senha")} value={addPasswordConfirm} onChange={(e) => setAddPasswordConfirm(e.target.value)} showIcon={false} className="h-11 rounded-xl border-[#E2E8F0]" />

            {renderPermissionModal(addAccessType, setAddAccessType, addFolderAccess, (m) => setAddFolderAccess(m), addPermissions, (s) => setAddPermissions(s))}
          </div>
          <DialogFooter className="gap-3 mt-0 pt-5">
            <Button variant="outline" onClick={() => setAddModalOpen(false)} className="h-11 px-6 rounded-full border-[#E2E8F0] text-[#475569] font-medium hover:bg-background hover:text-[#475569]" disabled={creating}>{translateInterfaceText("Cancelar")}</Button>
            <Button onClick={handleAdd} className="h-11 px-6 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium" disabled={creating}>
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{translateInterfaceText("Criando...")}</> : translateInterfaceText("Adicionar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Membro */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="w-[640px] min-w-[600px] max-w-[640px] rounded-2xl p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">{translateInterfaceText("Editar Membro")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {selectedMember && (
              <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-xl">
                <div className="w-12 h-12 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#2563EB]">{getInitials(selectedMember.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#0F172A] truncate">{selectedMember.name}</p>
                  <p className="text-sm text-[#64748B] truncate">{selectedMember.email}</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="editName" className="text-sm font-semibold text-[#1E293B]">{translateInterfaceText("Nome")}</Label>
              <Input id="editName" value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11 rounded-xl border-[#E2E8F0]" />
            </div>

            {renderPermissionModal(editAccessType, setEditAccessType, editFolderAccess, (m) => setEditFolderAccess(m), editPermissions, (s) => setEditPermissions(s))}
          </div>
          <DialogFooter className="gap-3 mt-0 pt-5">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="h-11 px-6 rounded-full border-[#E2E8F0] text-[#475569] font-medium hover:bg-background hover:text-[#475569]">{translateInterfaceText("Cancelar")}</Button>
            <Button onClick={handleEditSave} className="h-11 px-6 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium">{translateInterfaceText("Salvar alterações")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RemoveMemberModal
        open={removeModalOpen}
        onOpenChange={setRemoveModalOpen}
        memberName={selectedMember?.name || ''}
        onConfirm={handleRemoveConfirm}
      />
    </Layout>
  );
}
